import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Receipt, DownloadSimple, X } from "@phosphor-icons/react";

const BADGE = {
  autorizada: "bg-green-600 text-white",
  rejeitada: "bg-red-600 text-white",
  cancelada: "bg-zinc-800 text-white",
  aguardando_processamento: "bg-yellow-500 text-black",
  em_digitacao: "bg-blue-500 text-white",
  denegada: "bg-red-800 text-white",
};

/**
 * Célula da coluna "NFe" da página Pedidos.
 * - Faz lookup lazy da NF-e ativa do pedido (endpoint `/fiscal/nfe-by-order/{id}`).
 * - Se ok=true: mostra badge com status e abre modal ao clicar.
 * - No modal: dados da NF-e + botão pra baixar DANFE.
 * - Silencioso em 403 (usuário sem `fiscal` ou `nfe_view` não vê nada).
 */
export default function OrderNfeCell({ orderId, orderNumber }) {
  const [nfe, setNfe] = useState(undefined); // undefined = loading, null = none, obj = has
  const [showModal, setShowModal] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/fiscal/nfe-by-order/${orderId}`);
        if (alive) setNfe(data.nfe || null);
      } catch (e) {
        if (alive) setNfe(null);
        console.debug("[OrderNfeCell] falha:", e?.response?.status || e?.message);
      }
    })();
    return () => { alive = false; };
  }, [orderId]);

  const download = async (e) => {
    e?.stopPropagation();
    if (downloading || !nfe) return;
    setDownloading(true);
    try {
      const res = await api.get(`/fiscal/nfe-by-order/${orderId}/danfe.pdf`, { responseType: "blob" });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = href;
      a.download = `danfe_${nfe.number || orderNumber || orderId}.pdf`;
      a.click();
      URL.revokeObjectURL(href);
    } catch (err) {
      toast.error("DANFE indisponível: " + (err?.response?.data?.detail || "verifique se a NF-e foi autorizada"));
    } finally {
      setDownloading(false);
    }
  };

  if (nfe === undefined) return <span className="text-zinc-300 text-xs">…</span>;
  if (nfe === null) return <span className="text-zinc-300" data-testid={`order-nfe-none-${orderNumber}`}>—</span>;

  const label = (nfe.status || "").replace(/_/g, " ");
  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setShowModal(true); }}
        className={`text-[10px] font-mono px-2 py-0.5 uppercase hover:opacity-80 ${BADGE[nfe.status] || "bg-zinc-200"}`}
        title={`Clique para abrir a NF-e ${nfe.number || ""}`}
        data-testid={`order-nfe-badge-${orderNumber}`}
      >
        {nfe.number ? `NF ${nfe.number}` : label}
      </button>

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white border border-black w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid={`order-nfe-modal-${orderNumber}`}>
            <div className="border-b border-black px-5 py-4 flex items-start justify-between">
              <div>
                <div className="overline text-zinc-500 text-xs">Nota Fiscal Eletrônica</div>
                <div className="font-head font-black text-lg">NF-e nº {nfe.number || "—"}</div>
                <div className="text-xs text-zinc-500 mt-1">Pedido #{orderNumber}</div>
              </div>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-zinc-100" data-testid={`order-nfe-modal-close-${orderNumber}`}>
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <Row label="Status">
                <span className={`text-[10px] font-mono px-2 py-0.5 uppercase ${BADGE[nfe.status] || "bg-zinc-200"}`}>{label}</span>
              </Row>
              <Row label="Série">{nfe.series || "1"}</Row>
              <Row label="Valor">R$ {Number(nfe.total_value || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</Row>
              <Row label="Cliente">{nfe.customer || "—"}</Row>
              {nfe.customer_document && <Row label="CPF/CNPJ" mono>{nfe.customer_document}</Row>}
              {nfe.access_key && <Row label="Chave" mono small>{nfe.access_key}</Row>}
              {nfe.rejection_reason && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 text-red-700 text-xs">
                  <b>Motivo da rejeição:</b> {nfe.rejection_reason}
                </div>
              )}
            </div>
            <div className="border-t border-black px-5 py-3 flex justify-end gap-2">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm border border-black hover:bg-zinc-100">Fechar</button>
              {nfe.status === "autorizada" && (
                <button
                  onClick={download}
                  disabled={downloading}
                  className="px-4 py-2 text-sm bg-black text-white font-bold flex items-center gap-2 disabled:opacity-50"
                  data-testid={`order-nfe-download-${orderNumber}`}
                >
                  <DownloadSimple size={14} weight="bold" />
                  {downloading ? "Baixando…" : "Baixar DANFE"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Row({ label, children, mono, small }) {
  return (
    <div className="flex items-baseline gap-3">
      <div className="overline text-[10px] text-zinc-500 w-24 shrink-0">{label}</div>
      <div className={`flex-1 ${mono ? "font-mono" : ""} ${small ? "text-xs break-all" : ""}`}>{children}</div>
    </div>
  );
}
